// @core/stores/affiliate/AffiliateCommissionTierStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  CommissionTier,
  CreateCommissionTierRequest,
  UpdateCommissionTierRequest,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliateCommissionTierStore {
  // List state
  items: CommissionTier[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: CommissionTier | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, CommissionTier>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): CommissionTier | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: CommissionTier | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create commission tier
   */
  async CreateCommissionTier(data: CreateCommissionTierRequest): Promise<CommissionTier> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.CreateCommissionTier(data);
      const item = response.tier;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Get commission tier by ID
   */
  async GetCommissionTier(tier_id: string): Promise<CommissionTier> {
    // Check cache first
    const cached = this.cache.get(tier_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await affiliateApi.GetCommissionTier(tier_id);
      const item = response.tier;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List commission tiers
   */
  async ListCommissionTiers(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListCommissionTiers(params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update commission tier
   */
  async UpdateCommissionTier(tier_id: string, data: UpdateCommissionTierRequest): Promise<CommissionTier> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.UpdateCommissionTier(tier_id, data);
      const item = response.tier;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const affiliateCommissionTierStore = new AffiliateCommissionTierStore();
